import { useState, useEffect, useRef } from 'react';
import { Search, Terminal, FileJson, Braces, Hash, Key, Fingerprint, AtSign, FileCode, Type, AlignLeft, FileText, Files, Scissors, FileUser, Image, Clock, Twitter, MessageSquare, Webhook, QrCode, Palette, Camera, Smile, ImageIcon, Maximize, Crop, RefreshCw, Ruler, FileSpreadsheet, Timer, Gauge, Droplets, Lock, Eraser, RotateCw, ScanLine, BarChart3, Shuffle, Hourglass } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface Tool {
  name: string;
  path: string;
  icon: React.ElementType;
  category: string;
}

const allTools: Tool[] = [
  // Developer Tools
  { name: 'Code Studio', path: '/dashboard/tools/code-studio', icon: Terminal, category: 'Developer' },
  { name: 'JSON Formatter', path: '/dashboard/tools/json-formatter', icon: FileJson, category: 'Developer' },
  { name: 'Base64 Tool', path: '/dashboard/tools/base64', icon: Braces, category: 'Developer' },
  { name: 'Hash Generator', path: '/dashboard/tools/hash-generator', icon: Hash, category: 'Developer' },
  { name: 'Password Generator', path: '/dashboard/tools/password-generator', icon: Key, category: 'Developer' },
  { name: 'UUID Generator', path: '/dashboard/tools/uuid-generator', icon: Fingerprint, category: 'Developer' },

  // Email Tools
  { name: 'Temp Mail', path: '/dashboard/tools/temp-mail', icon: AtSign, category: 'Email' },

  // Document Tools
  { name: 'Word Counter', path: '/dashboard/tools/word-counter', icon: FileCode, category: 'Document' },
  { name: 'Lorem Ipsum', path: '/dashboard/tools/lorem-ipsum', icon: Type, category: 'Document' },
  { name: 'Text Case Converter', path: '/dashboard/tools/text-case', icon: AlignLeft, category: 'Document' },
  { name: 'Markdown to HTML', path: '/dashboard/tools/markdown-html', icon: FileText, category: 'Document' },
  { name: 'PDF Merge', path: '/dashboard/tools/pdf-merge', icon: Files, category: 'Document' },
  { name: 'PDF Splitter', path: '/dashboard/tools/pdf-splitter', icon: Scissors, category: 'Document' },
  { name: 'Resume Builder', path: '/dashboard/tools/resume-builder', icon: FileUser, category: 'Document' },
  { name: 'PDF to Image', path: '/dashboard/tools/pdf-to-image', icon: Image, category: 'Document' },

  // Social Tools
  { name: 'Discord Timestamp', path: '/dashboard/tools/discord-timestamp', icon: Clock, category: 'Social' },
  { name: 'Twitter Counter', path: '/dashboard/tools/twitter-counter', icon: Twitter, category: 'Social' },
  { name: 'Discord Embed Builder', path: '/dashboard/tools/discord-embed', icon: MessageSquare, category: 'Social' },
  { name: 'Webhook Sender', path: '/dashboard/tools/webhook-sender', icon: Webhook, category: 'Social' },

  // Design Tools
  { name: 'QR Code Generator', path: '/dashboard/tools/qr-code', icon: QrCode, category: 'Design' },
  { name: 'Image Compressor', path: '/dashboard/tools/image-compressor', icon: ImageIcon, category: 'Design' },
  { name: 'Color Picker', path: '/dashboard/tools/color-picker', icon: Palette, category: 'Design' },
  { name: 'Favicon Generator', path: '/dashboard/tools/favicon-generator', icon: Image, category: 'Design' },
  { name: 'Screenshot Beautifier', path: '/dashboard/tools/screenshot-beautifier', icon: Camera, category: 'Design' },
  { name: 'Emoji Picker', path: '/dashboard/tools/emoji-picker', icon: Smile, category: 'Design' },
  { name: 'PDF Editor', path: '/dashboard/tools/pdf-editor', icon: FileText, category: 'PDF' },
  { name: 'Image Resizer', path: '/dashboard/tools/image-resizer', icon: Maximize, category: 'Design' },
  { name: 'Image Cropper', path: '/dashboard/tools/image-cropper', icon: Crop, category: 'Design' },
  { name: 'Image Converter', path: '/dashboard/tools/image-converter', icon: RefreshCw, category: 'Design' },
  { name: 'Unit Converter', path: '/dashboard/tools/unit-converter', icon: Ruler, category: 'Document' },
  { name: 'CSV to JSON', path: '/dashboard/tools/csv-json', icon: FileSpreadsheet, category: 'Developer' },
  { name: 'Timestamp Converter', path: '/dashboard/tools/timestamp-converter', icon: Timer, category: 'Developer' },
  { name: 'PDF Compressor', path: '/dashboard/tools/pdf-compressor', icon: Gauge, category: 'PDF' },
  { name: 'PDF Password', path: '/dashboard/tools/pdf-password', icon: Lock, category: 'PDF' },
  { name: 'PDF Watermark', path: '/dashboard/tools/pdf-watermark', icon: Droplets, category: 'PDF' },
  { name: 'Background Remover', path: '/dashboard/tools/background-remover', icon: Eraser, category: 'Design' },
  { name: 'Image Rotate & Flip', path: '/dashboard/tools/image-rotate-flip', icon: RotateCw, category: 'Design' },
  { name: 'Placeholder Image', path: '/dashboard/tools/placeholder-image', icon: ImageIcon, category: 'Design' },
  { name: 'QR Code Reader', path: '/dashboard/tools/qr-reader', icon: ScanLine, category: 'Utilities' },
  { name: 'Barcode Generator', path: '/dashboard/tools/barcode-generator', icon: BarChart3, category: 'Utilities' },
  { name: 'Random Picker', path: '/dashboard/tools/random-picker', icon: Shuffle, category: 'Utilities' },
  { name: 'Countdown Timer', path: '/dashboard/tools/countdown-timer', icon: Hourglass, category: 'Utilities' },
  { name: 'Pomodoro Timer', path: '/dashboard/tools/pomodoro-timer', icon: Clock, category: 'Utilities' },
];

const SearchBar = () => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredTools = query.length > 0
    ? allTools.filter(tool =>
      tool.name.toLowerCase().includes(query.toLowerCase()) ||
      tool.category.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 6)
    : [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || filteredTools.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredTools.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredTools.length) % filteredTools.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredTools[selectedIndex]) {
          handleSelect(filteredTools[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  const handleSelect = (tool: Tool) => {
    navigate(tool.path);
    setQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div className="relative max-w-md w-full">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
      <Input
        ref={inputRef}
        placeholder="Search tools..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(e.target.value.length > 0);
        }}
        onFocus={() => query.length > 0 && setIsOpen(true)}
        onKeyDown={handleKeyDown}
        className="pl-10 bg-background/50 border-border/50"
      />

      {/* Dropdown */}
      {isOpen && filteredTools.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 py-2 glass border border-border/50 rounded-lg shadow-lg z-50 overflow-hidden"
        >
          {filteredTools.map((tool, index) => (
            <button
              key={tool.path}
              onClick={() => handleSelect(tool)}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2 text-left transition-colors",
                index === selectedIndex
                  ? "bg-primary/20 text-primary"
                  : "hover:bg-muted/50"
              )}
            >
              <tool.icon className="h-4 w-4 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{tool.name}</p>
                <p className="text-xs text-muted-foreground">{tool.category}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {isOpen && query.length > 0 && filteredTools.length === 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 py-4 px-3 glass border border-border/50 rounded-lg shadow-lg z-50 text-center"
        >
          <p className="text-sm text-muted-foreground">No tools found</p>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
